import type { ComponentType, ReactNode } from 'react';
import {
  Archive,
  ArrowDown,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  Cloud,
  ExternalLink,
  Landmark,
  Layers,
  Minus,
  Network,
  Server,
  ShieldCheck,
} from 'lucide-react';
import { useTranslation } from '@solara/i18n';
import { Card, CardBody, CardHeader, CardTitle, cn } from '@solara/ui-shared';

type LucideIcon = ComponentType<{ className?: string }>;

interface FlowNode {
  key: string;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  tone: 'tenant' | 'solara' | 'tickstar' | 'peppol' | 'fta' | 'archive';
}

const TONE_CLASS: Record<FlowNode['tone'], { card: string; icon: string }> = {
  tenant:   { card: 'border-border bg-surface',        icon: 'text-fg-muted bg-surface-muted' },
  solara:   { card: 'border-brand-200 bg-brand-50/60', icon: 'text-brand-700 bg-brand-100' },
  tickstar: { card: 'border-amber-300 bg-amber-50/70', icon: 'text-amber-700 bg-amber-100' },
  peppol:   { card: 'border-border bg-surface',        icon: 'text-fg-muted bg-surface-muted' },
  fta:      { card: 'border-border bg-surface',        icon: 'text-fg-muted bg-surface-muted' },
  archive:  { card: 'border-border bg-surface',        icon: 'text-fg-muted bg-surface-muted' },
};

export default function AboutPage() {
  const { t } = useTranslation('tickstar');
  const { t: tP1 } = useTranslation('p1');

  const horizontalNodes: FlowNode[] = [
    {
      key: 'tenant',
      icon: Building2,
      title: t('about.diagram.tenant'),
      subtitle: 'ERP / portal · idempotency-key',
      tone: 'tenant',
    },
    {
      key: 'solara',
      icon: Server,
      title: t('about.diagram.solara'),
      subtitle: 'PINT-AE · RBAC · orchestration · audit',
      tone: 'solara',
    },
    {
      key: 'tickstar',
      icon: Cloud,
      title: t('about.diagram.tickstar'),
      subtitle: 'AS4 · SMP · network compliance',
      tone: 'tickstar',
    },
    {
      key: 'peppol',
      icon: Network,
      title: t('about.diagram.peppol'),
      subtitle: '4-corner · SBDH · UBL XML',
      tone: 'peppol',
    },
    {
      key: 'fta',
      icon: Landmark,
      title: t('about.diagram.fta'),
      subtitle: 'EmaraTax · TDD 14-day SLA',
      tone: 'fta',
    },
  ];

  const responsibility = t('about.responsibility.items', { returnObjects: true }) as Record<
    string,
    { name: string; solara: boolean; tickstar: boolean }
  >;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('about.title')}</h1>
        <p className="text-fg-muted">{t('about.subtitle')}</p>
      </header>

      {/* Architecture diagram */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-brand-600" />
            {t('about.title')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="space-y-6">
            <div className="flex flex-col gap-3 lg:grid lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr] lg:items-stretch lg:gap-2">
              <FlowCard node={horizontalNodes[0]!} />
              <FlowArrow />
              <FlowCard node={horizontalNodes[1]!} />
              <FlowArrow />
              <FlowCard node={horizontalNodes[2]!} />
              <FlowArrow />
              <FlowCard node={horizontalNodes[3]!} />
              <FlowArrow />
              <FlowCard node={horizontalNodes[4]!} />
            </div>

            {/* Vertical branch from Solara → WORM Archive (Solara owns the archive). */}
            <div className="flex flex-col items-center gap-2">
              <div className="hidden lg:flex items-center text-fg-subtle text-xs">
                <span className="me-2">{t('about.diagram.solara')}</span>
                <ArrowDown className="h-4 w-4" />
              </div>
              <div className="lg:hidden flex items-center text-fg-subtle text-xs">
                <ArrowDown className="h-4 w-4" />
              </div>
              <div className="w-full max-w-md">
                <FlowCard
                  node={{
                    key: 'archive',
                    icon: Archive,
                    title: t('about.diagram.archive'),
                    subtitle: 'COMPLIANCE-locked S3 · hash-chained · 7 years',
                    tone: 'archive',
                  }}
                />
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Who does what */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">{t('about.rolesTitle')}</h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <RoleCard
            tone="solara"
            title={t('about.responsibility.solaraHeader')}
            tagline={t('about.roles.solara')}
            bullets={[
              'Multi-tenant onboarding, RBAC and SSO via the admin console.',
              'ERP connectors (SAP, Oracle, MS Dynamics, NetSuite, Tally).',
              'PINT-AE Schematron validation against the FTA rule book.',
              'EmaraTax TDD bridge — submitted within the 14-day window.',
              '7-year WORM archive in COMPLIANCE-locked S3, hash-chained.',
              'AI line-item categorisation (UNSPSC, GL accounts) and recovery audit.',
            ]}
          />
          <RoleCard
            tone="tickstar"
            title={t('about.responsibility.tickstarHeader')}
            tagline={t('about.roles.tickstar')}
            bullets={[
              'Peppol AS4 dispatch and receipt across the UAE 4-corner model.',
              'SMP registration: participant lifecycle, SML activation, Directory publish.',
              'Peppol PKI certificate issuance (under OpenPeppol governance).',
              'OpenPeppol compliance reporting (White-label bundle obligation).',
              'EU hosting (Ireland) with 99.5% availability SLA · 24/7 support.',
              '90-day default retention — Solara keeps the 7-year archive separately.',
            ]}
          />
        </div>
      </section>

      {/* Responsibility matrix */}
      <Card>
        <CardHeader>
          <CardTitle>{t('about.responsibility.title')}</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">{tP1('about.capability')}</th>
                <th className="px-4 py-3 text-center">{t('about.responsibility.solaraHeader')}</th>
                <th className="px-4 py-3 text-center">{t('about.responsibility.tickstarHeader')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Object.entries(responsibility).map(([key, row]) => (
                <tr key={key}>
                  <td className="px-4 py-3 text-fg">
                    <bdi dir="auto">{row.name}</bdi>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {row.solara ? (
                      <Check className="mx-auto h-4 w-4 text-brand-600" />
                    ) : (
                      <Minus className="mx-auto h-4 w-4 text-slate-300" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {row.tickstar ? (
                      <Check className="mx-auto h-4 w-4 text-amber-600" />
                    ) : (
                      <Minus className="mx-auto h-4 w-4 text-slate-300" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {/* Footer note */}
      <Card>
        <CardBody className="grid gap-6 sm:grid-cols-2 pt-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <ShieldCheck className="h-4 w-4 text-brand-600" />
              <span>{tP1('about.brandPromise')}</span>
            </div>
            <p className="text-sm text-fg-muted">
              <bdi dir="auto">
                Solara is the Arabic-first surface UAE finance teams trust: clean UX, deep
                integrations and predictable e-invoicing. The Peppol heavy lifting is delegated
                to TickStar (Xero), but the customer never has to think about it.
              </bdi>
            </p>
          </div>
          <div className="space-y-2 sm:border-s sm:border-border sm:ps-6">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <span>{tP1('about.compliancePosture')}</span>
            </div>
            <p className="text-sm text-fg-muted">
              <bdi dir="auto">
                FTA-accredited service-provider stack with quarterly external audit, OCSP-pinned
                certificates and 7-year hash-chained archive — see live evidence in the{' '}
                <a
                  href="/reports"
                  className="inline-flex items-center gap-1 text-brand-700 underline-offset-2 hover:underline"
                >
                  {tP1('about.reportsConsole')}
                  <ExternalLink className="h-3 w-3 rtl-mirror" />
                </a>
                . TickStar is ISO/IEC 27001:2013 certified with 99.5% availability SLA.
              </bdi>
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function FlowCard({ node }: { node: FlowNode }) {
  const Icon = node.icon;
  const tones = TONE_CLASS[node.tone];
  return (
    <div className={cn('rounded-xl border p-3 flex items-start gap-3 h-full', tones.card)}>
      <div className={cn('h-9 w-9 rounded-lg grid place-items-center shrink-0', tones.icon)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-fg leading-tight">
          <bdi dir="auto">{node.title}</bdi>
        </div>
        <div className="text-[11px] text-fg-subtle mt-0.5 leading-snug">
          <bdi dir="auto">{node.subtitle}</bdi>
        </div>
      </div>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="flex items-center justify-center text-fg-subtle">
      <ArrowRight className="h-4 w-4 rtl-mirror hidden lg:block" />
      <ArrowDown className="h-4 w-4 lg:hidden" />
    </div>
  );
}

function RoleCard({
  tone,
  title,
  tagline,
  bullets,
}: {
  tone: 'solara' | 'tickstar';
  title: string;
  tagline: ReactNode;
  bullets: string[];
}) {
  const accent =
    tone === 'tickstar'
      ? 'border-amber-200 bg-amber-50/40'
      : 'border-brand-200 bg-brand-50/40';
  const dot = tone === 'tickstar' ? 'bg-amber-500' : 'bg-brand-500';
  return (
    <Card className={cn(accent)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className={cn('h-2.5 w-2.5 rounded-full', dot)} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 pt-0">
        <p className="text-sm text-fg">
          <bdi dir="auto">{tagline}</bdi>
        </p>
        <ul className="space-y-1.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-fg-muted">
              <CheckCircle2
                className={cn(
                  'h-4 w-4 shrink-0 mt-0.5',
                  tone === 'tickstar' ? 'text-amber-600' : 'text-brand-600',
                )}
              />
              <bdi dir="auto">{b}</bdi>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
